package top.wcpe.allincore.dsl.compiler.theme

import kotlin.math.floor
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt
import kotlin.math.roundToLong

/**
 * 颜色运算工具。
 *
 * 引擎颜色统一用 #AARRGGBB（8 位，含 alpha 前缀）字符串表示。本工具在 RGB 通道上做
 * HSV 运算，alpha 通道原样保留（派生色继承种子色的不透明度）。
 *
 * 核心是移植 @ant-design/colors 的 generate() 10 级色板算法（HSV 域，固定常数），供
 * [ThemeDerivation] 在编译期由少量种子色派生整套梯度色。纯函数、无状态、无第三方依赖。
 */
object ColorMath {

    // —— @ant-design/colors generate() 算法常数（HSV 域）——
    private const val HUE_STEP = 2                  // 色相步长（度）
    private const val SATURATION_STEP = 0.16        // 浅色侧饱和度步长
    private const val SATURATION_STEP2 = 0.05       // 深色侧饱和度步长
    private const val BRIGHTNESS_STEP1 = 0.05       // 浅色侧明度步长
    private const val BRIGHTNESS_STEP2 = 0.15       // 深色侧明度步长
    private const val LIGHT_COLOR_COUNT = 5         // 主色之前的浅色级数
    private const val DARK_COLOR_COUNT = 4          // 主色之后的深色级数

    /** ARGB 四通道，各 0..255。 */
    data class Argb(val a: Int, val r: Int, val g: Int, val b: Int)

    /** HSV：h 为 0..360，s/v 为 0..1。 */
    data class Hsv(val h: Double, val s: Double, val v: Double)

    /** 解析 #AARRGGBB 或 #RRGGBB（后者 alpha 视为 255）。大小写不敏感。 */
    fun parseArgb(hex: String): Argb {
        val s = hex.trim().removePrefix("#")
        return when (s.length) {
            8 -> Argb(
                s.substring(0, 2).toInt(16),
                s.substring(2, 4).toInt(16),
                s.substring(4, 6).toInt(16),
                s.substring(6, 8).toInt(16),
            )
            6 -> Argb(
                255,
                s.substring(0, 2).toInt(16),
                s.substring(2, 4).toInt(16),
                s.substring(4, 6).toInt(16),
            )
            else -> throw IllegalArgumentException("非法颜色字面量：$hex（应为 #AARRGGBB 或 #RRGGBB）")
        }
    }

    /** 输出 #AARRGGBB 大写十六进制。 */
    fun toHex(c: Argb): String {
        fun h2(v: Int) = v.coerceIn(0, 255).toString(16).uppercase().padStart(2, '0')
        return "#${h2(c.a)}${h2(c.r)}${h2(c.g)}${h2(c.b)}"
    }

    /** RGB（0..255）转 HSV。 */
    fun rgbToHsv(r: Int, g: Int, b: Int): Hsv {
        val rf = r / 255.0
        val gf = g / 255.0
        val bf = b / 255.0
        val mx = max(rf, max(gf, bf))
        val mn = min(rf, min(gf, bf))
        val d = mx - mn
        val v = mx
        val s = if (mx == 0.0) 0.0 else d / mx
        val h = when {
            d == 0.0 -> 0.0
            mx == rf -> ((gf - bf) / d + (if (gf < bf) 6 else 0)) * 60
            mx == gf -> ((bf - rf) / d + 2) * 60
            else -> ((rf - gf) / d + 4) * 60
        }
        return Hsv(h, s, v)
    }

    /** HSV 转 RGB（0..255）。h:0..360，s/v:0..1。 */
    fun hsvToRgb(h: Double, s: Double, v: Double): Triple<Int, Int, Int> {
        val hh = (h % 360 + 360) % 360 / 60.0
        val i = floor(hh).toInt()
        val f = hh - i
        val p = v * (1 - s)
        val q = v * (1 - f * s)
        val t = v * (1 - (1 - f) * s)
        val (rf, gf, bf) = when (i % 6) {
            0 -> Triple(v, t, p)
            1 -> Triple(q, v, p)
            2 -> Triple(p, v, t)
            3 -> Triple(p, q, v)
            4 -> Triple(t, p, v)
            else -> Triple(v, p, q)
        }
        return Triple((rf * 255).roundToInt(), (gf * 255).roundToInt(), (bf * 255).roundToInt())
    }

    /** 保留两位小数（对齐 ant 算法里的 toFixed(2)）。roundToLong 双端同 half-up 语义（FR-UI-061）。 */
    private fun round2(x: Double): Double = (x * 100.0).roundToLong() / 100.0

    private fun deriveHue(h: Double, i: Int, light: Boolean): Double {
        val hr = h.roundToLong().toInt()
        var hue = if (hr in 60..240) {
            if (light) hr - HUE_STEP * i else hr + HUE_STEP * i
        } else {
            if (light) hr + HUE_STEP * i else hr - HUE_STEP * i
        }
        if (hue < 0) hue += 360
        else if (hue >= 360) hue -= 360
        return hue.toDouble()
    }

    private fun deriveSaturation(h: Double, s: Double, i: Int, light: Boolean): Double {
        // 灰色（无色相、无饱和）不调整饱和度，避免引入彩色
        if (h.roundToLong().toInt() == 0 && s == 0.0) return s
        var sat = when {
            light -> s - SATURATION_STEP * i
            i == DARK_COLOR_COUNT -> s + SATURATION_STEP
            else -> s + SATURATION_STEP2 * i
        }
        if (sat > 1) sat = 1.0
        if (light && i == LIGHT_COLOR_COUNT && sat > 0.1) sat = 0.1
        if (sat < 0.06) sat = 0.06
        return round2(sat)
    }

    private fun deriveValue(v: Double, i: Int, light: Boolean): Double {
        var value = if (light) v + BRIGHTNESS_STEP1 * i else v - BRIGHTNESS_STEP2 * i
        if (value > 1) value = 1.0
        return round2(value)
    }

    /**
     * 生成 10 级色板：index 0 最浅、index 9 最深、index 5 为输入主色本身。
     * 返回 #AARRGGBB 列表（alpha 继承输入种子）。对齐 @ant-design/colors generate()。
     */
    fun generate(seedHex: String): List<String> {
        val seed = parseArgb(seedHex)
        val hsv = rgbToHsv(seed.r, seed.g, seed.b)
        val out = ArrayList<String>(10)
        // 浅色端：i 从 5 递减到 1
        for (i in LIGHT_COLOR_COUNT downTo 1) {
            val (r, g, b) = hsvToRgb(
                deriveHue(hsv.h, i, true),
                deriveSaturation(hsv.h, hsv.s, i, true),
                deriveValue(hsv.v, i, true),
            )
            out.add(toHex(Argb(seed.a, r, g, b)))
        }
        // 主色本身
        out.add(toHex(seed))
        // 深色端：i 从 1 递增到 4
        for (i in 1..DARK_COLOR_COUNT) {
            val (r, g, b) = hsvToRgb(
                deriveHue(hsv.h, i, false),
                deriveSaturation(hsv.h, hsv.s, i, false),
                deriveValue(hsv.v, i, false),
            )
            out.add(toHex(Argb(seed.a, r, g, b)))
        }
        return out
    }

    /**
     * 把前景色 fg 以 alpha（0..1）不透明度叠加到背景色 bg 上，返回不透明的 #AARRGGBB。
     * 用于由文本基色 + 背景基色合成多级文本/填充色（对齐 Ant 的 rgba 文本层级）。
     */
    fun alphaBlend(bgHex: String, fgHex: String, alpha: Double): String {
        val bg = parseArgb(bgHex)
        val fg = parseArgb(fgHex)
        val a = alpha.coerceIn(0.0, 1.0)
        fun mix(bv: Int, fv: Int) = (fv * a + bv * (1 - a)).roundToInt().coerceIn(0, 255)
        return toHex(Argb(255, mix(bg.r, fg.r), mix(bg.g, fg.g), mix(bg.b, fg.b)))
    }

    /** 用给定 alpha（0..1）覆盖颜色的 alpha 通道，RGB 不变。 */
    fun withAlpha(hex: String, alpha: Double): String {
        val c = parseArgb(hex)
        return toHex(c.copy(a = (alpha.coerceIn(0.0, 1.0) * 255).roundToInt()))
    }

    /** 在 HSV 明度上提升 amt（0..1），alpha 不变。供 DSL `lighten(color, amt)`。 */
    fun lighten(hex: String, amt: Double): String {
        val c = parseArgb(hex)
        val hsv = rgbToHsv(c.r, c.g, c.b)
        val (r, g, b) = hsvToRgb(hsv.h, hsv.s, (hsv.v + amt).coerceIn(0.0, 1.0))
        return toHex(Argb(c.a, r, g, b))
    }

    /** 在 HSV 明度上降低 amt（0..1），alpha 不变。供 DSL `darken(color, amt)`。 */
    fun darken(hex: String, amt: Double): String {
        val c = parseArgb(hex)
        val hsv = rgbToHsv(c.r, c.g, c.b)
        val (r, g, b) = hsvToRgb(hsv.h, hsv.s, (hsv.v - amt).coerceIn(0.0, 1.0))
        return toHex(Argb(c.a, r, g, b))
    }

    /** 依据相对亮度为给定底色挑选可读的前景文字色（黑或白）。 */
    fun contrastText(hex: String): String {
        val c = parseArgb(hex)
        val lum = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255.0
        return if (lum > 0.6) "#FF000000" else "#FFFFFFFF"
    }
}
